import type { AgentEdge } from '@saaga/shared/interfaces/agency-node.interface'
import type { Connection, Edge, EdgeChange } from '@xyflow/react'
import { addEdge, applyEdgeChanges } from '@xyflow/react'
import { useCallback, useState } from 'react'

import { generateEdgeId } from './agency-flow-layout.util'

interface UseAgencyFlowEdgesReturn {
  edgeList: AgentEdge[]
  setEdgeList: React.Dispatch<React.SetStateAction<AgentEdge[]>>
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (connection: Connection) => void
  updateEdgeLabel: (edgeId: string, label: string) => void
  addEdgeToList: (edge: AgentEdge) => void
  removeEdgesForNode: (nodeId: string) => void
}

export const useAgencyFlowEdges = (initialEdgeList?: AgentEdge[]): UseAgencyFlowEdgesReturn => {
  const [edgeList, setEdgeList] = useState<AgentEdge[]>(initialEdgeList ?? [])

  const onEdgesChange = useCallback((changes: EdgeChange[]): void => {
    setEdgeList((prevEdgeList) => {
      const result = applyEdgeChanges(changes, prevEdgeList as Edge[])
      return result as AgentEdge[]
    })
  }, [])

  const onConnect = useCallback((connection: Connection): void => {
    if (!connection.source || !connection.target) return
    const newEdge: AgentEdge = {
      id: generateEdgeId(connection.source, connection.target),
      source: connection.source,
      target: connection.target,
      label: '',
    }
    setEdgeList((prevEdgeList) => {
      const result = addEdge(newEdge as Edge, prevEdgeList as Edge[])
      return result as AgentEdge[]
    })
  }, [])

  const updateEdgeLabel = useCallback((edgeId: string, label: string): void => {
    setEdgeList((prevEdgeList) =>
      prevEdgeList.map((edge) => {
        if (edge.id !== edgeId) return edge
        return { ...edge, label }
      })
    )
  }, [])

  const addEdgeToList = useCallback((edge: AgentEdge): void => {
    setEdgeList((prevEdgeList) => [...prevEdgeList, edge])
  }, [])

  const removeEdgesForNode = useCallback((nodeId: string): void => {
    setEdgeList((prevEdgeList) =>
      prevEdgeList.filter((e) => e.source !== nodeId && e.target !== nodeId)
    )
  }, [])

  return {
    edgeList,
    setEdgeList,
    onEdgesChange,
    onConnect,
    updateEdgeLabel,
    addEdgeToList,
    removeEdgesForNode,
  }
}
